import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Canonical goal status values for Blume IEP progress tracking. */
export type GoalStatus = 'on-track' | 'ahead' | 'warning' | 'off-track' | 'completed' | 'not-started';

/** Size variants for the indicator. */
export type GoalStatusSize = 'sm' | 'md' | 'lg';

interface StatusConfig {
  label: string;
  bg: string;
  text: string;
}

/** Visual configuration per status. All colors come from semantic tokens. */
const STATUS: Record<GoalStatus, StatusConfig> = {
  'on-track': { label: 'On track', bg: 'bg-primary', text: 'text-white' },
  ahead: { label: 'Ahead', bg: 'bg-success', text: 'text-white' },
  warning: { label: 'Needs attention', bg: 'bg-warning', text: 'text-white' },
  'off-track': { label: 'Off track', bg: 'bg-danger', text: 'text-white' },
  completed: { label: 'Completed', bg: 'bg-success', text: 'text-white' },
  'not-started': { label: 'Not started', bg: 'bg-medium', text: 'text-white' },
};

/**
 * Canonical rendering of an IEP goal's progress status. Use this anywhere a goal status
 * is displayed so the app stays consistent across feature surfaces.
 *
 * Renders as a compact colored pill with a label. Status is communicated via text + color,
 * with an `aria-label` ensuring screen-reader accessibility even when the label is hidden.
 *
 * @example
 * <app-goal-status-indicator status="on-track" />
 *
 * @example
 * <app-goal-status-indicator status="warning" size="lg" [showLabel]="true" />
 *
 * @feature shared
 */
@Component({
  selector: 'app-goal-status-indicator',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline-block' },
  template: `
    <span
      class="inline-flex items-center rounded-full font-semibold"
      [class.px-2]="size() === 'sm'"
      [class.py-0.5]="size() === 'sm'"
      [class.text-xs]="size() === 'sm'"
      [class.px-3]="size() === 'md'"
      [class.py-1]="size() === 'md'"
      [class.text-sm]="size() === 'md'"
      [class.px-4]="size() === 'lg'"
      [class.py-1.5]="size() === 'lg'"
      [class.text-base]="size() === 'lg'"
      [class]="config().bg + ' ' + config().text"
      [attr.role]="'status'"
      [attr.aria-label]="config().label"
    >
      @if (showLabel()) {
        <span>{{ config().label }}</span>
      }
    </span>
  `,
})
export class GoalStatusIndicatorComponent {
  /** Goal status. */
  readonly status = input.required<GoalStatus>();

  /** Size. @default 'sm' */
  readonly size = input<GoalStatusSize>('sm');

  /** Show the label next to the icon. @default true */
  readonly showLabel = input<boolean>(true);

  /** Resolved visual config for the current status. */
  readonly config = computed(() => STATUS[this.status()]);
}
